import {ProviderLogo} from '@github-ui/copilot-byok/providers/ProviderLogo'
import {AUTO_MODEL_ID} from '@github-ui/copilot-chat-helpers/auto-model'
import {BASE_MODEL_ID, isCustomModel} from '@github-ui/copilot-chat-state/models'
import {type CopilotChatModel, CopilotPlan} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {matchesFocusVisible} from '@github-ui/focus-visible'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {Tooltip} from '@github-ui/pacer/Tooltip'
import {AiModelIcon, ChevronRightIcon, CopilotIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Label, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import React, {useRef, useState} from 'react'

import {AutoTierMenu, getConfiguredAutoTierLabel, type VisibleAutoTierPickerConfig} from './AutoTierPicker'
import {type ActiveModelPromo, useActiveModelPromo} from './use-active-model-promo'
import {getMinimumRequiredPlan, getModelLockState} from './model-picker-helpers'
import {ModelInfoPopover} from './ModelInfoPopover'
import styles from './ModelPicker.module.css'

/**
 * Formats a billing multiplier for display.
 * @param multiplier - The billing multiplier value
 * @returns A formatted string like "3x", "1.5x", "0.33x", or "0x" (default)
 */
function formatMultiplier(multiplier: number | undefined): string {
  // Default to 0 if no multiplier is defined
  const value = multiplier ?? 0

  // Convert to number to remove trailing zeros, then back to string
  return `${Number(value.toFixed(2))}x`
}

interface ModelListItemProps {
  model: CopilotChatModel
  selected: boolean
  onModelPicked: (m: CopilotChatModel) => void
  organizationDefault?: boolean
  useCatalogEligibility?: boolean
}

export const ModelListItem = React.memo(function ModelListItemComponent({
  model,
  selected,
  onModelPicked,
  organizationDefault = false,
  useCatalogEligibility = false,
  isTokenBasedBillingEnabled = false,
  autoPinnedModelDisplayName,
  autoPinnedModel,
  autoPinnedModelDiscount,
  autoModeDiscounts,
  autoTierPicker,
  autoTierPortalContainerName,
}: ModelListItemProps & {
  isTokenBasedBillingEnabled?: boolean
  autoPinnedModelDisplayName?: string
  autoPinnedModel?: CopilotChatModel
  autoPinnedModelDiscount?: number
  autoModeDiscounts?: Record<string, number>
  autoTierPicker?: VisibleAutoTierPickerConfig
  autoTierPortalContainerName?: string
}) {
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  const [isAutoTierMenuOpen, setIsAutoTierMenuOpen] = useState(false)
  const {plan, premiumInteractionsQuotaExceeded, chatQuotaExceeded, overagesEnabled, canUpgradePlan} = useEntitlement()
  const itemRef = useRef<HTMLLIElement>(null)
  const autoTierMenuLabelId = React.useId()
  const autoTierDescriptionId = React.useId()
  const {freePlanAndOutOfQuota, premiumModelAndOutOfQuota, modelOutOfPlan, isUpsellModel, locked} = getModelLockState(
    model,
    useCatalogEligibility ? 'catalog' : {plan, premiumInteractionsQuotaExceeded, chatQuotaExceeded, overagesEnabled},
  )
  const visibleAutoTierPicker = model.id === AUTO_MODEL_ID ? autoTierPicker : undefined
  const autoModelDisplayName = visibleAutoTierPicker?.autoModelDisplayName ?? model.displayName
  const effectiveTierLabel = visibleAutoTierPicker
    ? getConfiguredAutoTierLabel(visibleAutoTierPicker, visibleAutoTierPicker.effectiveTier)
    : undefined
  const selectedTierLabel = visibleAutoTierPicker
    ? getConfiguredAutoTierLabel(visibleAutoTierPicker, visibleAutoTierPicker.selectedTier)
    : undefined
  const pendingTierLabel = visibleAutoTierPicker
    ? getConfiguredAutoTierLabel(visibleAutoTierPicker, visibleAutoTierPicker.pendingTier)
    : undefined
  const autoTierDescription = pendingTierLabel
    ? effectiveTierLabel
      ? `Optimized for ${effectiveTierLabel} · Applying ${pendingTierLabel}…`
      : `Applying ${pendingTierLabel}…`
    : selectedTierLabel && selectedTierLabel !== effectiveTierLabel
      ? `${selectedTierLabel} selected for the next message`
      : effectiveTierLabel
        ? `Optimized for ${effectiveTierLabel}`
        : 'Choose an optimization for the next message'
  const autoTierTrailingLabel = pendingTierLabel
    ? `${pendingTierLabel} applying`
    : selectedTierLabel && selectedTierLabel !== effectiveTierLabel
      ? selectedTierLabel
      : (effectiveTierLabel ?? 'Choose')
  const showAutoTierMenu = visibleAutoTierPicker !== undefined && !locked
  const minRequiredPlan = getMinimumRequiredPlan(model)
  const isProPlusModel = !useCatalogEligibility && minRequiredPlan === CopilotPlan.IndividualProPlus
  const isMaxOnlyModel = !useCatalogEligibility && minRequiredPlan === CopilotPlan.IndividualMax
  const activePromo = useActiveModelPromo(model.billing?.promo)
  const visiblePromo = locked ? undefined : activePromo

  const lockedTooltipText = locked
    ? useCatalogEligibility || (!model.model_picker_enabled && !isUpsellModel)
      ? 'This model is currently unavailable.'
      : getLockedModelTooltip({
          isUpsellOrOutOfPlan: isUpsellModel || modelOutOfPlan,
          freePlanAndOutOfQuota,
          premiumModelAndOutOfQuota,
          isProPlusOnly: isProPlusModel,
          isMaxOnly: isMaxOnlyModel,
          canUpgradePlan,
        })
    : null

  // Render Multiplier when in Premium Request Unit (Auto pseudo-model has no real billing multiplier)
  const showMultiplierIndicators =
    model.id !== AUTO_MODEL_ID && !isCustomModel(model) && !isTokenBasedBillingEnabled && !isUpsellModel && !locked
  const multiplierLabelText = showMultiplierIndicators ? formatMultiplier(model.billing?.multiplier) : null
  const promoDiscountPercent = visiblePromo?.discount_percent
  const roundedPromoDiscountPercent =
    promoDiscountPercent !== undefined && Number.isFinite(promoDiscountPercent)
      ? Number(promoDiscountPercent.toFixed(2))
      : null
  const promoDiscountLabel =
    roundedPromoDiscountPercent !== null &&
    roundedPromoDiscountPercent > 0 &&
    promoDiscountPercent !== undefined &&
    promoDiscountPercent <= 100
      ? `${roundedPromoDiscountPercent}% off`
      : null

  // The model's info popover should be available only if the model has billable content to show (token prices or Auto's pinned model) and isn't locked
  const hasInfoContent = isTokenBasedBillingEnabled && (!!model.billing?.token_prices || model.id === AUTO_MODEL_ID)

  // On the Auto row, reveal the thread's pinned model on hover/focus with descriptive
  // tooltip copy that also serves as the row's accessible description. When the rich info
  // popover is active for the Auto row (token-based billing + billable info content), it already surfaces the
  // pinned model, so suppress this plain tooltip to avoid a double hover. The Auto row can
  // itself be locked (e.g. a free-plan user out of chat quota), in which case
  // lockedTooltipText takes priority — the quota message matters more than the pin reveal.
  const pinnedModelRowTooltip =
    model.id === AUTO_MODEL_ID && autoPinnedModelDisplayName && !hasInfoContent
      ? `${autoModelDisplayName} is currently using ${autoPinnedModelDisplayName}`
      : null
  // Bake the "Model locked." framing into the tooltip text so it doubles as the row's
  // accessible description. Locked takes priority over the pin reveal.
  const lockedRowTooltip = lockedTooltipText ? `Model locked. ${lockedTooltipText}` : null
  const baseRowTooltip = lockedRowTooltip ?? pinnedModelRowTooltip
  const rowTooltipText =
    baseRowTooltip && visiblePromo?.message && !/[.!?]$/.test(baseRowTooltip) ? `${baseRowTooltip}.` : baseRowTooltip
  const promoDescriptionId = React.useId()
  const infoDescriptionId = isInfoOpen && hasInfoContent && !lockedTooltipText ? `model-info-${model.id}` : undefined
  const itemDescriptionIds =
    [
      visiblePromo?.message ? promoDescriptionId : undefined,
      showAutoTierMenu && autoTierDescription ? autoTierDescriptionId : undefined,
      infoDescriptionId,
    ]
      .filter(Boolean)
      .join(' ') || undefined

  const item = (
    <ActionList.Item
      key={model.id}
      onSelect={showAutoTierMenu ? undefined : () => onModelPicked(model)}
      selected={selected}
      disabled={!!locked}
      aria-description={lockedRowTooltip ?? undefined}
      {...(itemDescriptionIds ? {'aria-describedby': itemDescriptionIds} : {})}
      ref={itemRef}
      onMouseEnter={() => {
        if (hasInfoContent && !locked) setIsInfoOpen(true)
      }}
      onMouseLeave={e => {
        // Keep open if the mouse moved into the popover. relatedTarget is the
        // overlay container (an ancestor of the content div), so we must check
        // the AnchoredOverlay root rather than the content div itself.
        const popoverEl = document.getElementById(`model-info-${model.id}`)
        const overlayContainer = popoverEl?.closest('[data-component="AnchoredOverlay"]')
        if (overlayContainer?.contains(e.relatedTarget as Node)) return
        setIsInfoOpen(false)
      }}
      onFocus={e => {
        if (!(e.relatedTarget instanceof HTMLLIElement) && !matchesFocusVisible(e.currentTarget)) return
        if (hasInfoContent && !locked) setIsInfoOpen(true)
      }}
      onBlur={() => setIsInfoOpen(false)}
    >
      <ActionList.LeadingVisual>
        <ModelVendorIcon model={model} />
      </ActionList.LeadingVisual>
      <span className={clsx(locked && styles.modelNameDisabled)}>
        {model.displayName}
        {model.vendor && <VisuallyHidden>, {model.vendor} provider</VisuallyHidden>}
      </span>
      {organizationDefault && <span className={styles.organizationDefaultLabel}>Org default</span>}
      {locked && isMaxOnlyModel && <span className={styles.maxBadge}>Max</span>}
      {locked && isProPlusModel && <span className={styles.proPlusBadge}>Pro+</span>}
      {model.preview && model.id !== BASE_MODEL_ID && <span className={styles.modelMetaLabel}>Preview</span>}
      {(visiblePromo?.message || (showAutoTierMenu && autoTierDescription)) && (
        <ActionList.Description variant="block">
          {visiblePromo?.message ? <ModelPromoMessage id={promoDescriptionId} promo={visiblePromo} /> : null}
          {visiblePromo?.message && autoTierDescription ? <span> · </span> : null}
          {showAutoTierMenu ? <span id={autoTierDescriptionId}>{autoTierDescription}</span> : null}
        </ActionList.Description>
      )}
      {promoDiscountLabel ? (
        <ActionList.TrailingVisual>
          <Label variant="accent">{promoDiscountLabel}</Label>
        </ActionList.TrailingVisual>
      ) : showMultiplierIndicators ? (
        <ActionList.TrailingVisual>
          <span className={styles.multiplier}>{multiplierLabelText}</span>
        </ActionList.TrailingVisual>
      ) : showAutoTierMenu ? (
        <ActionList.TrailingVisual>
          <span className={styles.autoTierTrailingVisual} aria-hidden="true">
            <span>{autoTierTrailingLabel}</span>
            <ChevronRightIcon />
          </span>
        </ActionList.TrailingVisual>
      ) : null}
    </ActionList.Item>
  )

  const infoPopover =
    hasInfoContent && !locked ? (
      <ModelInfoPopover
        model={model}
        autoPinnedModel={autoPinnedModel}
        autoPinnedModelDisplayName={model.id === AUTO_MODEL_ID ? autoPinnedModelDisplayName : undefined}
        autoPinnedModelDiscount={autoPinnedModelDiscount}
        autoModeDiscounts={model.id === AUTO_MODEL_ID ? autoModeDiscounts : undefined}
        autoModelDisplayName={autoModelDisplayName}
        anchorRef={itemRef}
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
    ) : null

  const itemWithTooltip = rowTooltipText ? (
    <Tooltip text={rowTooltipText} position="top">
      {item}
    </Tooltip>
  ) : (
    item
  )

  const renderedItem = showAutoTierMenu ? (
    <ActionMenu
      anchorRef={itemRef}
      open={isAutoTierMenuOpen}
      onOpenChange={open => {
        setIsAutoTierMenuOpen(open)
        if (open) setIsInfoOpen(false)
      }}
    >
      <ActionMenu.Anchor>{itemWithTooltip}</ActionMenu.Anchor>
      {/* Allow bounded submenus to overlap their anchor rather than overflow the host. */}
      <ActionMenu.Overlay
        width="medium"
        className={styles.actionMenuOverlay}
        maxHeight="fit-content"
        variant={{regular: 'anchored', narrow: autoTierPortalContainerName ? 'anchored' : 'fullscreen'}}
        portalContainerName={autoTierPortalContainerName}
        displayInViewport={autoTierPortalContainerName ? false : undefined}
        side={autoTierPortalContainerName ? 'inside-bottom' : undefined}
        aria-labelledby={autoTierMenuLabelId}
      >
        <span id={autoTierMenuLabelId} className="sr-only">
          Auto model optimization
        </span>
        <AutoTierMenu config={visibleAutoTierPicker} onBack={() => setIsAutoTierMenuOpen(false)} />
      </ActionMenu.Overlay>
    </ActionMenu>
  ) : (
    itemWithTooltip
  )

  return (
    <>
      {renderedItem}
      {infoPopover}
    </>
  )
})

function ModelPromoMessage({id, promo}: {id: string; promo: ActiveModelPromo}) {
  if (!promo.message) return null

  const {messageBeforeEndDate, formattedEndDate, endDateLocale} = promo
  if (!messageBeforeEndDate || !formattedEndDate || !endDateLocale) {
    return <span id={id}>{promo.message}</span>
  }

  return (
    <span id={id}>
      {messageBeforeEndDate} Ends <span lang={endDateLocale}>{formattedEndDate}</span>.
    </span>
  )
}

function getLockedModelTooltip({
  isUpsellOrOutOfPlan,
  freePlanAndOutOfQuota,
  premiumModelAndOutOfQuota,
  isProPlusOnly,
  isMaxOnly,
  canUpgradePlan,
}: {
  isUpsellOrOutOfPlan: boolean
  freePlanAndOutOfQuota: boolean
  premiumModelAndOutOfQuota: boolean
  isProPlusOnly: boolean
  isMaxOnly: boolean
  canUpgradePlan: boolean
}): string {
  if (freePlanAndOutOfQuota) return "You've used all free requests this month."
  if (premiumModelAndOutOfQuota) return "You've reached your premium request limit this month."
  const planName = isMaxOnly
    ? 'Copilot Max'
    : isProPlusOnly
      ? 'Copilot Pro+'
      : isUpsellOrOutOfPlan
        ? 'Copilot Pro'
        : null
  if (!planName) return 'This model is not available on your plan.'
  return canUpgradePlan ? `Upgrade to ${planName} to access this model` : `This model requires ${planName}`
}

// `decorative` renders the icon without an accessible name (empty alt). Use it where the
// model is already named by adjacent visible/sr-only text — e.g. the trigger button — so
// the vendor logo isn't announced redundantly. Octicons are always aria-hidden regardless.
export function ModelVendorIcon({model, decorative = false}: {model: CopilotChatModel; decorative?: boolean}) {
  // Auto is the model-routing pseudo-model; always show the AI model icon even if a
  // CAPI-sourced Auto entry arrives carrying a vendor logoURL.
  if (model.id === AUTO_MODEL_ID) {
    return <AiModelIcon />
  }

  if (isCustomModel(model) && model.custom_model.provider) {
    return <ProviderLogo provider={model.custom_model.provider} />
  }

  return model.logoURL ? (
    <GitHubAvatar
      alt={decorative ? '' : `${model.vendor} logo`}
      square
      size={18}
      src={model.logoURL}
      className={styles.vendorIcon}
    />
  ) : (
    <CopilotIcon />
  )
}
